import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { CoachingClip, CoachingNote, Player, PlayerGoal } from "@/lib/types";
import { errorMessage } from "@/lib/api";
import { useMe } from "@/features/auth/useMe";
import {
  useCoachUsers,
  useRoster,
  useRosterMutations,
} from "./hooks/useRoster";
import {
  computeRosterKpis,
  filterRoster,
  formatNotesPerPlayer,
  type RosterStatusFilter,
} from "./rosterMetrics";
import { PlayerModal } from "./components/PlayerModal";
import { LinkAccountModal } from "./components/LinkAccountModal";
import { LinkAccountPickerModal } from "./components/LinkAccountPickerModal";
import { RosterImportModal } from "./components/RosterImportModal";
import { PageHeader } from "./components/PageHeader";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";
import { useCoachClipsList } from "./hooks/useCoachClipsList";
import { useCoachGoalsList } from "./hooks/useCoachGoalsList";
import { useCoachNotesList } from "./hooks/useCoachNotesList";

/**
 * Coach > Players list page.
 *
 * Phase 2 shifts the old Roster page into a Player Feedback Home:
 * the dominant surface is now "what should I do for this player?",
 * while roster administration stays available in the management
 * panel and existing modals.
 *
 * Layout (restored from the legacy two-pane design):
 *   - Header — descriptive title + `Import CSV` (team-admin gated)
 *     and `Add Player` (focuses the Quick Add form).
 *   - Two-pane body — the roster table on the left, a persistent
 *     `Quick Add Player` form on the right. Quick Add is a true
 *     inline form, NOT a modal: name + jersey + position + an
 *     optional account link, one `Add to Roster` button. The full
 *     `PlayerModal` is still used for detailed EDITS (per-row
 *     pencil) where the active toggle and longer fields matter.
 *
 * KPIs reflect the FULL roster (not the filtered view) — legacy
 * behavior. The notes-per-player KPI needs every team note, so
 * this page also reads `getCoachingNotes()` (unfiltered).
 */

export type RosterPageProps = {
  /** Opens the simplified feedback capture flow for a player. */
  onGiveFeedback: (playerId?: string) => void;
  /** Opens the goal composer (create mode) preselected to this
   * player. Undefined ⇒ the row's goal button is hidden. Owned by
   * `CoachShell` so the goal + development modals stay
   * one-at-a-time. */
  onCreatePlayerGoal?: (playerId: string) => void;
  /** Opens the Player Development modal for a player. Owned by
   * `CoachShell` (same one-modal-at-a-time reason). */
  onOpenDevelopment: (playerId: string, playerName: string) => void;
};

const STATUS_FILTERS: ReadonlyArray<{
  value: RosterStatusFilter;
  label: string;
}> = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

type PlayerAttentionFilter =
  | "all"
  | "needs_follow_up"
  | "need_help"
  | "no_recent_feedback"
  | "no_family_link"
  | "goals_stale";

type PlayerArrivalContext =
  | "need_help"
  | "tried_this"
  | "no_recent_feedback"
  | "unreviewed_feedback"
  | "stale_goal"
  | "missing_recap";

const ATTENTION_FILTERS: ReadonlyArray<{
  value: PlayerAttentionFilter;
  label: string;
}> = [
  { value: "all", label: "All players" },
  { value: "needs_follow_up", label: "Needs follow-up" },
  { value: "need_help", label: "Need help" },
  { value: "no_recent_feedback", label: "No recent feedback" },
  { value: "no_family_link", label: "No family link" },
  { value: "goals_stale", label: "Goals stale" },
];

/** Common positions for the Quick Add form. The roster data model
 * has no `position` column — the legacy roster overloads the
 * free-text `notes` field as the position, so these write straight
 * into `notes`. An empty value leaves `notes` blank. */
const POSITION_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "", label: "—" },
  { value: "Goalkeeper", label: "Goalkeeper" },
  { value: "Defender", label: "Defender" },
  { value: "Midfielder", label: "Midfielder" },
  { value: "Forward", label: "Forward" },
];

export function RosterPage({
  onGiveFeedback,
  onCreatePlayerGoal,
  onOpenDevelopment,
}: RosterPageProps) {
  const navigate = useNavigate();
  const routeSearch = useSearch({ from: "/coach" });
  const toast = useToast();
  const me = useMe().data;
  // CSV roster import is a roster mutation — the backend gates both
  // import endpoints on the `roster:manage` capability (coach,
  // team_admin, global_admin; NOT assistant_coach). Gate the launch
  // button on the same capability so a coach sees it but an
  // assistant coach — who has read-only roster access — does not.
  const isGlobalAdmin = !!me?.user?.is_global_admin;
  const canImportRoster =
    isGlobalAdmin ||
    !!me?.active_scope?.capabilities?.includes("roster:manage");
  const rosterQuery = useRoster();
  const mutations = useRosterMutations();

  const notesQuery = useCoachNotesList();
  const clipsQuery = useCoachClipsList();
  const goalsQuery = useCoachGoalsList();

  const players = useMemo(
    () => rosterQuery.data?.players ?? [],
    [rosterQuery.data],
  );
  const notes = useMemo(
    () => notesQuery.data?.notes ?? [],
    [notesQuery.data],
  );
  const clips = useMemo(
    () => clipsQuery.data?.clips ?? [],
    [clipsQuery.data],
  );
  const goals = useMemo(
    () => goalsQuery.data?.goals ?? [],
    [goalsQuery.data],
  );
  const kpis = useMemo(
    () => computeRosterKpis(players, notes),
    [players, notes],
  );

  // ---- Search + status filter ----
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<RosterStatusFilter>("all");
  const [attentionFilter, setAttentionFilter] =
    useState<PlayerAttentionFilter>("all");
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [managementOpen, setManagementOpen] = useState(false);
  useEffect(() => {
    if (routeSearch.player_id) {
      setSelectedPlayerId(routeSearch.player_id);
    }
  }, [routeSearch.player_id]);
  const filtered = useMemo(
    () =>
      filterPlayerFeedbackHome({
        players: filterRoster(players, search, statusFilter),
        notes,
        clips,
        goals,
        filter: attentionFilter,
      }),
    [attentionFilter, clips, goals, notes, players, search, statusFilter],
  );
  const selectedPlayer = useMemo(() => {
    return (
      players.find((p) => p.id === selectedPlayerId) ?? filtered[0] ?? null
    );
  }, [filtered, players, selectedPlayerId]);
  const selectPlayer = (playerId: string) => {
    setSelectedPlayerId(playerId);
    void navigate({
      to: "/coach",
      search: { tab: "roster", player_id: playerId },
    });
  };

  // ---- Modal state ----
  // `editModal`: the player being edited (full PlayerModal). Quick
  // Add is the inline sidebar form, not a modal. `linkModal`: the
  // player being linked. `linkPickerOpen`: the header "Link Account"
  // flow — a player-picker that, on continue, populates `linkModal`.
  const [editModal, setEditModal] = useState<Player | null>(null);
  const [linkModal, setLinkModal] = useState<Player | null>(null);
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Player | null>(null);

  // The Quick Add sidebar form's name input — the header "Add
  // Player" button focuses it so the entry point still reads as a
  // top-level action.
  const quickAddNameRef = useRef<HTMLInputElement | null>(null);
  const focusQuickAdd = () => {
    setManagementOpen(true);
    quickAddNameRef.current?.focus();
    quickAddNameRef.current?.scrollIntoView({
      block: "center",
      behavior: "smooth",
    });
  };
  const openManagementAndFocusAdd = () => {
    setManagementOpen(true);
    window.setTimeout(focusQuickAdd, 0);
  };

  const handleAddObservation = (playerId: string) => {
    // The M4→M6 carry-in: route to Coach Review's tactical-board
    // surface with the player preselected. Coach Review reads
    // `?source` + `?player_id` from the search params.
    navigate({
      to: "/coach/review",
      search: { source: "tactical_board", player_id: playerId },
    });
  };

  const confirmDeletePlayer = async () => {
    if (!confirmDelete) return;
    const player = confirmDelete;
    setConfirmDelete(null);
    setDeleting(player.id);
    try {
      await mutations.deletePlayer(player.id);
      toast.success("Player deleted");
    } catch (err) {
      toast.error("Couldn't delete player", { description: errorMessage(err) });
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="flex flex-col gap-6" data-testid="roster-page">
      {confirmDelete ? (
        <ConfirmDialog
          title="Delete player?"
          message={`Delete ${confirmDelete.display_name}? This removes the player and their account links.`}
          confirmLabel="Delete"
          danger
          onConfirm={confirmDeletePlayer}
          onClose={() => setConfirmDelete(null)}
          data-testid="roster-delete-confirm"
        />
      ) : null}
      <PageHeader
        title="Players"
        description="Open a player, understand what they need next, then create feedback or follow up with the right context."
        actions={
          <>
            <button
              type="button"
              onClick={() =>
                onGiveFeedback(selectedPlayer?.id)
              }
              data-testid="players-give-feedback"
              className="inline-flex items-center gap-1.5 rounded bg-accent px-3 py-1.5 text-xs font-display uppercase tracking-wider text-white hover:bg-accent-hover transition-colors"
            >
              <ObservationIcon />
              Give feedback
            </button>
            <button
              type="button"
              onClick={() => setManagementOpen((open) => !open)}
              data-testid="roster-management-toggle"
              className="inline-flex items-center gap-1.5 rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink hover:bg-surface transition-colors"
            >
              <EditIcon />
              Manage players
            </button>
            <button
              type="button"
              onClick={openManagementAndFocusAdd}
              data-testid="roster-add-player"
              className="inline-flex items-center gap-1.5 rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink hover:bg-surface transition-colors"
            >
              <PlusIcon />
              Add Player
            </button>
          </>
        }
      />

      {/* KPI tiles — reflect the full roster, not the filtered view. */}
      <div
        className="grid grid-cols-2 gap-3 lg:grid-cols-4"
        data-testid="roster-kpis"
      >
        <KpiTile label="Active Players" value={String(kpis.activePlayers)} />
        <KpiTile
          label="Linked Accounts"
          value={String(kpis.linkedAccounts)}
          delta={
            kpis.linkedAccountsThisWeek > 0
              ? `+${kpis.linkedAccountsThisWeek} this week`
              : undefined
          }
        />
        <KpiTile
          label="Without Family Link"
          value={String(kpis.withoutLink)}
        />
        <KpiTile
          label="Avg. Notes / Player"
          value={formatNotesPerPlayer(kpis.notesPerPlayer)}
        />
      </div>

      {/* Toolbar: player search + progressive filters. */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search player, jersey, position, linked account..."
          data-testid="roster-search"
          className="w-full min-w-0 bg-bg-card border border-rule rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent sm:min-w-[14rem] sm:flex-1"
        />
        <div
          className="flex w-full gap-1 rounded-md border border-rule bg-bg-card p-1 sm:w-auto"
          role="group"
          aria-label="Status filter"
        >
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatusFilter(f.value)}
              data-roster-filter={f.value}
              data-active={statusFilter === f.value}
              className={[
                "rounded px-3 py-1 text-xs font-display uppercase tracking-wide transition-colors",
                statusFilter === f.value
                  ? "bg-accent text-white"
                  : "text-ink-muted hover:text-ink",
              ].join(" ")}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div
        className="flex gap-1 overflow-x-auto rounded-md border border-rule bg-bg-card p-1"
        role="group"
        aria-label="Player attention filter"
        data-testid="player-attention-filters"
      >
        {ATTENTION_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setAttentionFilter(f.value)}
            data-player-filter={f.value}
            data-active={attentionFilter === f.value}
            className={[
              "whitespace-nowrap rounded px-3 py-1 text-xs font-display uppercase tracking-wide transition-colors",
              attentionFilter === f.value
                ? "bg-accent text-white"
                : "text-ink-muted hover:text-ink",
            ].join(" ")}
          >
            {f.label}
          </button>
        ))}
      </div>

      {rosterQuery.isLoading ? (
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="roster-loading"
        >
          Loading players...
        </p>
      ) : players.length === 0 ? (
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="roster-empty"
        >
          No players yet. Open player administration to add the first one.
        </p>
      ) : filtered.length === 0 ? (
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="roster-no-match"
        >
          No players match your filters.{" "}
          <button
            type="button"
            onClick={() => {
              setSearch("");
              setStatusFilter("all");
              setAttentionFilter("all");
            }}
            className="text-accent hover:underline"
          >
            Clear filters
          </button>
        </p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(16rem,22rem)_1fr]">
          <section
            className="min-w-0 rounded-lg border border-rule bg-bg-card"
            aria-label="Players"
            data-testid="roster-card-list"
          >
            <header className="border-b border-rule px-3 py-3">
              <h2 className="text-sm font-display uppercase tracking-wider text-ink-muted">
                Player queue
              </h2>
            </header>
            <div className="flex max-h-[38rem] flex-col gap-1 overflow-y-auto p-2">
              {filtered.map((player) => (
                <PlayerQueueItem
                  key={player.id}
                  player={player}
                  selected={selectedPlayer?.id === player.id}
                  status={buildPlayerFeedbackStatus(player, notes, clips, goals)}
                  onSelect={() => selectPlayer(player.id)}
                />
              ))}
            </div>
          </section>

          {selectedPlayer ? (
            <PlayerFeedbackHome
              player={selectedPlayer}
              notes={notes}
              clips={clips}
              goals={goals}
              arrivalContext={routeSearch.context}
              deleting={deleting === selectedPlayer.id}
              onGiveFeedback={() =>
                onGiveFeedback(selectedPlayer.id)
              }
              onAddObservation={() => handleAddObservation(selectedPlayer.id)}
              onOpenDevelopment={() =>
                onOpenDevelopment(selectedPlayer.id, selectedPlayer.display_name)
              }
              onCreateGoal={
                onCreatePlayerGoal
                  ? () => onCreatePlayerGoal(selectedPlayer.id)
                  : undefined
              }
              onLink={() => setLinkModal(selectedPlayer)}
              onUnlink={async (linkId) => {
                try {
                  await mutations.unlinkAccount(linkId);
                  toast.success("Account unlinked");
                } catch (err) {
                  toast.error("Couldn't unlink account", {
                    description: errorMessage(err),
                  });
                }
              }}
              onEdit={() => setEditModal(selectedPlayer)}
              onDelete={() => setConfirmDelete(selectedPlayer)}
            />
          ) : null}
        </div>
      )}

      {managementOpen ? (
        <section
          className="rounded-lg border border-rule bg-bg-card p-4"
          data-testid="roster-management-panel"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule pb-3">
            <div>
              <h2 className="font-display text-base text-ink">
                Player administration
              </h2>
              <p className="mt-1 text-sm text-ink-muted">
                Add players, import CSVs, and manage family/account links.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {canImportRoster ? (
                <button
                  type="button"
                  onClick={() => setImportOpen(true)}
                  data-testid="roster-import-open"
                  className="inline-flex items-center gap-1.5 rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink hover:bg-surface transition-colors"
                >
                  <ImportIcon />
                  Import CSV
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setLinkPickerOpen(true)}
                data-testid="roster-link-open"
                className="inline-flex items-center gap-1.5 rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink hover:bg-surface transition-colors"
              >
                <LinkIcon />
                Link Account
              </button>
            </div>
          </div>
          <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(16rem,1fr)_18rem]">
            <div className="min-w-0 rounded border border-rule bg-surface p-3">
              <h3 className="text-sm font-display uppercase tracking-wider text-ink-muted">
                Selected player tools
              </h3>
              {selectedPlayer ? (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="mr-auto min-w-[10rem] text-sm text-ink">
                    {selectedPlayer.display_name}
                  </span>
                  <RosterActionButtons
                    deleting={deleting === selectedPlayer.id}
                    onAddObservation={() => handleAddObservation(selectedPlayer.id)}
                    onOpenDevelopment={() =>
                      onOpenDevelopment(
                        selectedPlayer.id,
                        selectedPlayer.display_name,
                      )
                    }
                    onCreateGoal={
                      onCreatePlayerGoal
                        ? () => onCreatePlayerGoal(selectedPlayer.id)
                        : undefined
                    }
                    onLink={() => setLinkModal(selectedPlayer)}
                    onEdit={() => setEditModal(selectedPlayer)}
                    onDelete={() => setConfirmDelete(selectedPlayer)}
                    align="start"
                  />
                </div>
              ) : (
                <p className="mt-3 text-sm text-ink-muted">
                  Select a player to manage details.
                </p>
              )}
            </div>
            <QuickAddPanel
              mutations={mutations}
              nameRef={quickAddNameRef}
              onAdded={() => toast.success("Player added")}
            />
          </div>
        </section>
      ) : null}

      {editModal ? (
        <PlayerModal
          player={editModal}
          mutations={mutations}
          onClose={() => setEditModal(null)}
        />
      ) : null}
      {/* Header "Link Account" → pick a player, then hand off to the
       * same LinkAccountModal the per-row link action uses. */}
      {linkPickerOpen ? (
        <LinkAccountPickerModal
          players={players}
          onPick={(player) => {
            setLinkPickerOpen(false);
            setLinkModal(player);
          }}
          onClose={() => setLinkPickerOpen(false)}
        />
      ) : null}
      {linkModal ? (
        <LinkAccountModal
          player={linkModal}
          mutations={mutations}
          onClose={() => setLinkModal(null)}
        />
      ) : null}
      {importOpen ? (
        <RosterImportModal onClose={() => setImportOpen(false)} />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Player Feedback Home — Phase 2 player-first roster replacement
// ---------------------------------------------------------------------------

type PlayerFeedbackStatus = {
  lastFeedbackAt: string | null;
  lastFeedbackLabel: string;
  needsFollowUp: boolean;
  needsHelp: number;
  triedThis: number;
  noRecentFeedback: boolean;
  noFamilyLink: boolean;
  activeGoals: number;
  staleGoals: number;
  nextAction: string;
  nextActionDetail: string;
};

function PlayerQueueItem({
  player,
  selected,
  status,
  onSelect,
}: {
  player: Player;
  selected: boolean;
  status: PlayerFeedbackStatus;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        "rounded-md border px-3 py-3 text-left transition-colors",
        selected
          ? "border-accent bg-accent/10"
          : "border-transparent hover:border-rule hover:bg-surface",
      ].join(" ")}
      data-testid="roster-row"
      data-player-id={player.id}
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex h-8 min-w-8 items-center justify-center rounded-full bg-surface px-1 text-xs font-mono text-accent">
          {player.jersey_number || "--"}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-display text-sm text-ink">
            {player.display_name}
          </span>
          <span className="mt-1 block truncate text-xs text-ink-muted">
            {status.nextAction}
          </span>
        </span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        {status.needsHelp > 0 ? <AttentionPill tone="flag" label="Need help" /> : null}
        {status.triedThis > 0 ? <AttentionPill tone="good" label="Tried this" /> : null}
        {status.noRecentFeedback ? (
          <AttentionPill tone="default" label="No recent" />
        ) : null}
        {status.noFamilyLink ? (
          <AttentionPill tone="default" label="No link" />
        ) : null}
        {status.staleGoals > 0 ? <AttentionPill tone="accent" label="Goal stale" /> : null}
      </div>
    </button>
  );
}

function PlayerFeedbackHome({
  player,
  notes,
  clips,
  goals,
  arrivalContext,
  deleting,
  onGiveFeedback,
  onAddObservation,
  onOpenDevelopment,
  onCreateGoal,
  onLink,
  onUnlink,
  onEdit,
  onDelete,
}: RosterRowProps & {
  notes: CoachingNote[];
  clips: CoachingClip[];
  goals: PlayerGoal[];
  arrivalContext?: PlayerArrivalContext;
  onGiveFeedback: () => void;
}) {
  const status = buildPlayerFeedbackStatus(player, notes, clips, goals);
  const arrivalCopy = arrivalContext
    ? playerArrivalContextCopy(arrivalContext)
    : null;
  const playerGoals = goalsForPlayer(goals, player.id);
  const recentFeedback = feedbackForPlayer(notes, clips, player.id).slice(0, 4);
  const activeGoals = playerGoals.filter(isActiveGoal).slice(0, 3);

  return (
    <section
      className="min-w-0 rounded-lg border border-rule bg-bg-card"
      data-testid="player-feedback-home"
      data-player-id={player.id}
    >
      <header className="border-b border-rule px-4 py-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex h-9 min-w-9 items-center justify-center rounded-full bg-surface px-1 text-sm font-mono text-accent">
                {player.jersey_number || "--"}
              </span>
              <h2 className="truncate font-display text-2xl text-ink">
                {player.display_name}
              </h2>
              <span
                className={[
                  "rounded-full px-2 py-0.5 text-xs",
                  player.active
                    ? "bg-good/15 text-good"
                    : "bg-surface text-ink-muted",
                ].join(" ")}
              >
                {player.active ? "Active" : "Inactive"}
              </span>
            </div>
            {player.notes?.trim() ? (
              <p className="mt-2 text-sm uppercase tracking-wide text-ink-muted">
                {player.notes.trim()}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onGiveFeedback}
              className="inline-flex items-center gap-1.5 rounded bg-accent px-3 py-1.5 text-xs font-display uppercase tracking-wider text-white hover:bg-accent-hover transition-colors"
            >
              <ObservationIcon />
              Give feedback
            </button>
            {onCreateGoal ? (
              <button
                type="button"
                onClick={onCreateGoal}
                className="inline-flex items-center gap-1.5 rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink hover:bg-surface transition-colors"
              >
                <GoalIcon />
                Create goal
              </button>
            ) : null}
          </div>
        </div>
      </header>

      <div className="grid gap-4 p-4 xl:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="min-w-0 space-y-4">
          {arrivalCopy ? (
            <section
              className="rounded border border-accent/40 bg-accent/10 p-3"
              data-testid="player-arrival-context"
            >
              <h3 className="text-sm font-display uppercase tracking-wider text-accent-ink">
                {arrivalCopy.title}
              </h3>
              <p className="mt-1 text-sm leading-5 text-ink-muted">
                {arrivalCopy.detail}
              </p>
            </section>
          ) : null}

          <section className="rounded border border-rule bg-surface p-3">
            <h3 className="text-sm font-display uppercase tracking-wider text-ink-muted">
              Next coach action
            </h3>
            <p className="mt-2 font-display text-xl text-ink">
              {status.nextAction}
            </p>
            <p className="mt-1 text-sm leading-5 text-ink-muted">
              {status.nextActionDetail}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onGiveFeedback}
                className="rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink hover:bg-bg-card transition-colors"
              >
                Give feedback
              </button>
              <button
                type="button"
                onClick={onOpenDevelopment}
                className="rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink hover:bg-bg-card transition-colors"
              >
                Open plan
              </button>
              <button
                type="button"
                onClick={onAddObservation}
                className="rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink hover:bg-bg-card transition-colors"
              >
                Add observation
              </button>
            </div>
          </section>

          <section className="rounded border border-rule bg-surface p-3">
            <h3 className="text-sm font-display uppercase tracking-wider text-ink-muted">
              Recent feedback
            </h3>
            {recentFeedback.length === 0 ? (
              <p className="mt-3 text-sm text-ink-muted">
                No player-facing notes or clips are attached to this player yet.
              </p>
            ) : (
              <div className="mt-3 space-y-2">
                {recentFeedback.map((item) => (
                  <div
                    key={`${item.kind}-${item.id}`}
                    className="rounded border border-rule bg-bg-card px-3 py-2"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-display text-sm text-ink">
                        {item.title}
                      </span>
                      <span className="rounded-full bg-surface px-2 py-0.5 text-[0.68rem] uppercase tracking-wider text-ink-muted">
                        {item.kind}
                      </span>
                      <span className="text-xs text-ink-muted">
                        {formatShortDate(item.at)}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-ink-muted">
                      {item.detail}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <aside className="space-y-4">
          <section className="rounded border border-rule bg-surface p-3">
            <h3 className="text-sm font-display uppercase tracking-wider text-ink-muted">
              Player plan
            </h3>
            {activeGoals.length === 0 ? (
              <p className="mt-3 text-sm text-ink-muted">
                No active goals are open for this player.
              </p>
            ) : (
              <div className="mt-3 space-y-2">
                {activeGoals.map((goal) => (
                  <div key={goal.id} className="rounded border border-rule bg-bg-card p-2">
                    <div className="font-display text-sm text-ink">
                      {goal.title}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      <AttentionPill
                        tone={goal.needs_coach_follow_up ? "flag" : "accent"}
                        label={goal.status.replaceAll("_", " ")}
                      />
                      {isStaleGoal(goal) ? (
                        <AttentionPill tone="flag" label="Stale" />
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="rounded border border-rule bg-surface p-3">
            <h3 className="text-sm font-display uppercase tracking-wider text-ink-muted">
              Did it land?
            </h3>
            <div className="mt-3">
              <RosterResponseChips
                summary={player.signal_summary}
                onOpenDevelopment={onOpenDevelopment}
              />
            </div>
            <p className="mt-3 text-xs leading-5 text-ink-muted">
              Last feedback: {status.lastFeedbackLabel}
            </p>
          </section>

          <section className="rounded border border-rule bg-surface p-3">
            <h3 className="text-sm font-display uppercase tracking-wider text-ink-muted">
              Accounts
            </h3>
            <AccountLinks player={player} onLink={onLink} onUnlink={onUnlink} />
            <div className="mt-3 border-t border-rule pt-3">
              <RosterActionButtons
                deleting={deleting}
                onAddObservation={onAddObservation}
                onOpenDevelopment={onOpenDevelopment}
                onCreateGoal={onCreateGoal}
                onLink={onLink}
                onEdit={onEdit}
                onDelete={onDelete}
                align="start"
              />
            </div>
          </section>
        </aside>
      </div>
    </section>
  );
}

function AccountLinks({
  player,
  onLink,
  onUnlink,
}: {
  player: Player;
  onLink: () => void;
  onUnlink: (linkId: number) => void;
}) {
  const links = player.links ?? [];
  if (links.length === 0) {
    return (
      <div className="mt-3">
        <p className="text-sm text-ink-muted">No family or player account linked.</p>
        <button
          type="button"
          onClick={onLink}
          className="mt-2 rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink hover:bg-bg-card transition-colors"
        >
          Link account
        </button>
      </div>
    );
  }
  return (
    <div className="mt-3 flex flex-wrap gap-1">
      {links.map((link) => (
        <button
          key={link.id}
          type="button"
          onClick={() => onUnlink(link.id)}
          data-testid="roster-link-chip"
          title={`Unlink @${link.username} - ${link.relationship}`}
          className="inline-flex items-center gap-1 rounded-full border border-rule bg-bg-card px-2 py-0.5 text-xs text-ink hover:border-destructive hover:text-destructive transition-colors"
        >
          <span className="text-ink-muted">{link.relationship}</span>
          <span>@{link.username}</span>
          <span aria-hidden="true">x</span>
        </button>
      ))}
    </div>
  );
}

function AttentionPill({
  tone,
  label,
}: {
  tone: "default" | "accent" | "flag" | "good";
  label: string;
}) {
  const classes = {
    default: "bg-surface text-ink-muted",
    accent: "bg-accent/15 text-accent-ink",
    flag: "bg-flag text-navy",
    good: "bg-good/15 text-good",
  }[tone];
  return (
    <span
      className={[
        "inline-flex rounded-full px-2 py-0.5 text-[0.68rem] font-display uppercase tracking-wider",
        classes,
      ].join(" ")}
    >
      {label}
    </span>
  );
}

function playerArrivalContextCopy(context: PlayerArrivalContext): {
  title: string;
  detail: string;
} {
  if (context === "need_help") {
    return {
      title: "Arrived from: need-help response",
      detail:
        "Start with a focused reply or a simpler next step so the feedback lands.",
    };
  }
  if (context === "tried_this") {
    return {
      title: "Arrived from: tried-this response",
      detail:
        "Close the loop by recognizing the attempt and naming the next cue.",
    };
  }
  if (context === "no_recent_feedback") {
    return {
      title: "Arrived from: no recent feedback",
      detail:
        "Create one clear player-facing touch, then attach evidence only if it helps.",
    };
  }
  if (context === "unreviewed_feedback") {
    return {
      title: "Arrived from: shared but not reviewed",
      detail:
        "Check whether the player or family needs a nudge, a simpler summary, or a different evidence package.",
    };
  }
  if (context === "stale_goal") {
    return {
      title: "Arrived from: goal needs attention",
      detail:
        "Update the plan or create feedback tied to the current goal.",
    };
  }
  return {
    title: "Arrived from: missing recap",
    detail:
      "Use the match context to decide whether this player needs a recap, review session, or direct feedback.",
  };
}

function filterPlayerFeedbackHome({
  players,
  notes,
  clips,
  goals,
  filter,
}: {
  players: Player[];
  notes: CoachingNote[];
  clips: CoachingClip[];
  goals: PlayerGoal[];
  filter: PlayerAttentionFilter;
}): Player[] {
  if (filter === "all") return players;
  return players.filter((player) => {
    const status = buildPlayerFeedbackStatus(player, notes, clips, goals);
    if (filter === "needs_follow_up") return status.needsFollowUp;
    if (filter === "need_help") return status.needsHelp > 0;
    if (filter === "no_recent_feedback") return status.noRecentFeedback;
    if (filter === "no_family_link") return status.noFamilyLink;
    return status.staleGoals > 0;
  });
}

function buildPlayerFeedbackStatus(
  player: Player,
  notes: CoachingNote[],
  clips: CoachingClip[],
  goals: PlayerGoal[],
): PlayerFeedbackStatus {
  const feedback = feedbackForPlayer(notes, clips, player.id);
  const playerGoals = goalsForPlayer(goals, player.id);
  const activeGoals = playerGoals.filter(isActiveGoal);
  const staleGoals = activeGoals.filter(isStaleGoal);
  const lastFeedbackAt = feedback[0]?.at ?? null;
  const noRecentFeedback =
    !lastFeedbackAt || Date.now() - Date.parse(lastFeedbackAt) > 21 * 24 * 60 * 60 * 1000;
  const needsHelp = player.signal_summary?.need_help ?? 0;
  const triedThis = player.signal_summary?.tried_this ?? 0;
  const goalFollowUp = activeGoals.some((goal) => goal.needs_coach_follow_up);
  const noFamilyLink = (player.links ?? []).length === 0;

  if (needsHelp > 0) {
    return {
      lastFeedbackAt,
      lastFeedbackLabel: lastFeedbackAt ? formatShortDate(lastFeedbackAt) : "none yet",
      needsFollowUp: true,
      needsHelp,
      triedThis,
      noRecentFeedback,
      noFamilyLink,
      activeGoals: activeGoals.length,
      staleGoals: staleGoals.length,
      nextAction: "Reply to a need-help response",
      nextActionDetail:
        "A linked player or guardian asked for help. Start by clarifying the cue or giving a smaller next step.",
    };
  }
  if (goalFollowUp || staleGoals.length > 0) {
    return {
      lastFeedbackAt,
      lastFeedbackLabel: lastFeedbackAt ? formatShortDate(lastFeedbackAt) : "none yet",
      needsFollowUp: true,
      needsHelp,
      triedThis,
      noRecentFeedback,
      noFamilyLink,
      activeGoals: activeGoals.length,
      staleGoals: staleGoals.length,
      nextAction: "Update the player plan",
      nextActionDetail:
        "A goal needs coach attention. Connect the next feedback touch to the current plan.",
    };
  }
  if (triedThis > 0) {
    return {
      lastFeedbackAt,
      lastFeedbackLabel: lastFeedbackAt ? formatShortDate(lastFeedbackAt) : "none yet",
      needsFollowUp: true,
      needsHelp,
      triedThis,
      noRecentFeedback,
      noFamilyLink,
      activeGoals: activeGoals.length,
      staleGoals: staleGoals.length,
      nextAction: "Close the loop",
      nextActionDetail:
        "The player says they tried the feedback. Reinforce what changed and name the next cue.",
    };
  }
  if (noRecentFeedback) {
    return {
      lastFeedbackAt,
      lastFeedbackLabel: lastFeedbackAt ? formatShortDate(lastFeedbackAt) : "none yet",
      needsFollowUp: false,
      needsHelp,
      triedThis,
      noRecentFeedback,
      noFamilyLink,
      activeGoals: activeGoals.length,
      staleGoals: staleGoals.length,
      nextAction: "Give a fresh feedback touch",
      nextActionDetail:
        "There is no recent player-facing feedback. Start with one clear message and optional evidence.",
    };
  }
  if (noFamilyLink) {
    return {
      lastFeedbackAt,
      lastFeedbackLabel: lastFeedbackAt ? formatShortDate(lastFeedbackAt) : "none yet",
      needsFollowUp: false,
      needsHelp,
      triedThis,
      noRecentFeedback,
      noFamilyLink,
      activeGoals: activeGoals.length,
      staleGoals: staleGoals.length,
      nextAction: "Link a family account",
      nextActionDetail:
        "Player administration is secondary here, but linking an account helps player-facing feedback reach the right people.",
    };
  }
  return {
    lastFeedbackAt,
    lastFeedbackLabel: lastFeedbackAt ? formatShortDate(lastFeedbackAt) : "none yet",
    needsFollowUp: false,
    needsHelp,
    triedThis,
    noRecentFeedback,
    noFamilyLink,
    activeGoals: activeGoals.length,
    staleGoals: staleGoals.length,
    nextAction: "Plan the next useful touch",
    nextActionDetail:
      "Use a note, clip, goal, or review session when the next coaching moment is ready.",
  };
}

function feedbackForPlayer(
  notes: CoachingNote[],
  clips: CoachingClip[],
  playerId: string,
) {
  const noteItems = notes
    .filter((note) => note.player_ids?.includes(playerId))
    .map((note) => ({
      kind: note.tactical_board_json ? "observation" : "note",
      id: note.id,
      title: note.player_summary || note.title || "Coaching note",
      detail: note.what_to_do_next || note.category || note.visibility,
      at: note.updated_at || note.created_at,
    }));
  const clipItems = clips
    .filter((clip) => clip.player_ids?.includes(playerId))
    .map((clip) => ({
      kind: "clip",
      id: clip.id,
      title: clip.title || "Coaching clip",
      detail: clip.description || clip.category || clip.visibility,
      at: clip.updated_at || clip.created_at,
    }));
  return [...noteItems, ...clipItems].sort((a, b) =>
    String(b.at).localeCompare(String(a.at)),
  );
}

function goalsForPlayer(goals: PlayerGoal[], playerId: string) {
  return goals.filter((goal) => goal.player_id === playerId);
}

function isActiveGoal(goal: PlayerGoal) {
  return goal.status !== "achieved" && goal.status !== "archived";
}

function isStaleGoal(goal: PlayerGoal) {
  if (!isActiveGoal(goal) || !goal.target_date) return false;
  const target = Date.parse(goal.target_date);
  return Number.isFinite(target) && target < Date.now();
}

function formatShortDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Quick Add Player — the always-visible inline sidebar form
// ---------------------------------------------------------------------------

/**
 * Persistent "Quick Add Player" form. Restores the legacy roster's
 * inline add panel — no modal, just a form pinned beside the table.
 *
 * Captures the fast common case: display name + jersey + position +
 * an optional family / self account link. After a successful add the
 * form resets and refocuses the name input so a coach can add
 * players in a quick run. Detailed edits (active toggle, etc.) still
 * go through `PlayerModal` per-row.
 *
 * The account link is a two-step write — `createPlayer` then
 * `linkAccount` — because the create endpoint has no link field. The
 * relationship defaults to `guardian` (the common case); the per-row
 * link modal is the place to pick `self` / `other`.
 */
function QuickAddPanel({
  mutations,
  nameRef,
  onAdded,
}: {
  mutations: ReturnType<typeof useRosterMutations>;
  /** The header "Add Player" button focuses this input. Mutable
   * `current` so the panel can refocus it after a successful add. */
  nameRef: React.MutableRefObject<HTMLInputElement | null>;
  onAdded: () => void;
}) {
  const toast = useToast();
  // The team's linkable users — fed to the "Link family / self
  // account" select. Always enabled: the panel is always mounted.
  const usersQuery = useCoachUsers(true);
  const linkableUsers = usersQuery.data?.users ?? [];
  const [name, setName] = useState("");
  const [jersey, setJersey] = useState("");
  const [position, setPosition] = useState("");
  const [linkUserId, setLinkUserId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = name.trim();
    if (!clean) {
      setError("Display name is required.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const created = await mutations.createPlayer({
        display_name: clean,
        jersey_number: jersey.trim(),
        // The roster has no `position` column — `notes` carries it.
        notes: position,
        active: true,
      });
      // Optional account link — a second write, since `createPlayer`
      // has no link field. A failed link must not lose the created
      // player, so it surfaces as its own toast and the add still
      // counts as a success.
      if (linkUserId) {
        try {
          await mutations.linkAccount({
            player_id: created.player.id,
            user_id: linkUserId,
            relationship: "guardian",
          });
        } catch (linkErr) {
          toast.error("Player added, but the account link failed", {
            description: errorMessage(linkErr),
          });
        }
      }
      onAdded();
      // Reset for the next quick add and refocus the name field.
      setName("");
      setJersey("");
      setPosition("");
      setLinkUserId("");
      nameRef.current?.focus();
    } catch (err) {
      setError(errorMessage(err));
      toast.error("Couldn't add player", { description: errorMessage(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <aside
      className="w-full shrink-0 xl:w-72"
      data-testid="roster-quick-add-panel"
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-3 rounded-lg border border-rule bg-bg-card p-4"
      >
        <h3 className="text-sm font-display uppercase tracking-wider text-ink-muted">
          Quick Add Player
        </h3>

        <QuickAddField label="Display name">
          <input
            ref={nameRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Jordan Reyes"
            data-testid="roster-quick-add-name"
            className="w-full bg-surface border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </QuickAddField>

        <QuickAddField label="Jersey number">
          <input
            type="text"
            value={jersey}
            onChange={(e) => setJersey(e.target.value)}
            placeholder="optional"
            data-testid="roster-quick-add-jersey"
            className="w-full bg-surface border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </QuickAddField>

        <QuickAddField label="Position" helper="optional">
          <TkticSelect
            value={position}
            onChange={setPosition}
            data-testid="roster-quick-add-position"
            size="sm"
            ariaLabel="Position"
            options={POSITION_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
          />
        </QuickAddField>

        <QuickAddField label="Link family / self account" helper="optional">
          <TkticSelect
            value={linkUserId}
            onChange={setLinkUserId}
            data-testid="roster-quick-add-link"
            size="sm"
            ariaLabel="Link family / self account"
            disabled={usersQuery.isLoading}
            options={[
              {
                value: "",
                label: usersQuery.isLoading
                  ? "Loading accounts…"
                  : linkableUsers.length === 0
                    ? "— none —"
                    : "— none —",
              },
              ...linkableUsers.map((u) => ({
                value: u.id,
                label: `${u.display_name} · @${u.username}`,
              })),
            ]}
          />
        </QuickAddField>

        {error ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="roster-quick-add-error"
          >
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          data-testid="roster-quick-add-submit"
          className="inline-flex items-center justify-center gap-1.5 rounded bg-accent px-4 py-2 text-sm font-display uppercase tracking-wide text-white hover:bg-accent-hover disabled:bg-bg-card disabled:text-ink-muted transition-colors"
        >
          <PlusIcon />
          {submitting ? "Adding…" : "Add player"}
        </button>

        <p className="rounded border border-rule bg-surface px-3 py-2 text-xs text-ink-muted">
          <span className="font-display uppercase tracking-wider text-accent">
            Tip
          </span>{" "}
          — Players are separate from login users. Linking a family
          account lets a parent see that player's private feedback. The link
          here is added as a guardian; use the row link action to set self /
          other.
        </p>
      </form>
    </aside>
  );
}

function QuickAddField(props: {
  label: string;
  helper?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
        {props.label}
        {props.helper ? (
          <span className="ml-1 normal-case tracking-normal text-ink-muted/70">
            ({props.helper})
          </span>
        ) : null}
      </span>
      {props.children}
    </label>
  );
}

function KpiTile(props: { label: string; value: string; delta?: string }) {
  return (
    <div className="rounded-lg border border-rule bg-bg-card px-4 py-3">
      <div className="text-ink-muted text-xs font-display uppercase tracking-wider">
        {props.label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <strong className="text-2xl font-display text-ink tabular-nums">
          {props.value}
        </strong>
        {props.delta ? (
          <span className="text-xs text-accent">{props.delta}</span>
        ) : null}
      </div>
    </div>
  );
}

type RosterRowProps = {
  player: Player;
  deleting: boolean;
  onAddObservation: () => void;
  /** Opens the M7 Player Development profile modal for this player. */
  onOpenDevelopment: () => void;
  /** Undefined ⇒ the goal button is hidden (Goals slice wires it). */
  onCreateGoal?: () => void;
  onLink: () => void;
  onUnlink: (linkId: number) => void;
  onEdit: () => void;
  onDelete: () => void;
};

function RosterActionButtons({
  deleting,
  onAddObservation,
  onOpenDevelopment,
  onCreateGoal,
  onLink,
  onEdit,
  onDelete,
  align,
}: Omit<RosterRowProps, "player" | "onUnlink"> & { align: "start" | "end" }) {
  return (
    <div
      className={[
        "inline-flex w-max flex-nowrap items-center gap-0.5 rounded-md border border-rule bg-surface p-0.5",
        align === "end" ? "justify-end" : "justify-start",
      ].join(" ")}
      data-testid="roster-action-bar"
    >
      <IconButton
        label="Add observation"
        testid="roster-action-observation"
        onClick={onAddObservation}
        icon={<ObservationIcon />}
      />
      <IconButton
        label="Player plan"
        testid="roster-action-development"
        onClick={onOpenDevelopment}
        icon={<ChartIcon />}
      />
      {onCreateGoal ? (
        <IconButton
          label="Create goal"
          testid="roster-action-goal"
          onClick={onCreateGoal}
          icon={<GoalIcon />}
        />
      ) : null}
      <IconButton
        label="Link account"
        testid="roster-action-link"
        onClick={onLink}
        icon={<LinkIcon />}
      />
      <IconButton
        label="Edit"
        testid="roster-action-edit"
        onClick={onEdit}
        icon={<EditIcon />}
      />
      <IconButton
        label="Delete"
        testid="roster-action-delete"
        onClick={onDelete}
        disabled={deleting}
        danger
        icon={<TrashIcon />}
      />
    </div>
  );
}

/** Compact square icon button for a roster row action. The text
 * label is exposed via `title` + `aria-label` so the icon-only
 * control stays accessible and discoverable on hover. */
function IconButton(props: {
  label: string;
  testid: string;
  icon: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props.testid}
      title={props.label}
      aria-label={props.label}
      className={[
        "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded border transition-colors disabled:opacity-40",
        props.danger
          ? "border-rule text-ink-muted hover:border-destructive hover:text-destructive hover:bg-destructive/10"
          : "border-rule text-ink-muted hover:border-accent hover:text-accent hover:bg-accent/10",
      ].join(" ")}
    >
      {props.icon}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Inline SVG icons — the app has no icon library; surfaces draw
// their own (cf. `ChevronIcon` in `TkticSelect`). All 16×16,
// `currentColor` stroke so they inherit the button's text color.
// ---------------------------------------------------------------------------

const ICON_PROPS = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function PlusIcon() {
  return (
    <svg {...ICON_PROPS}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function ImportIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function ObservationIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg {...ICON_PROPS}>
      <line x1="6" y1="20" x2="6" y2="12" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="18" y1="20" x2="18" y2="14" />
    </svg>
  );
}

function GoalIcon() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
      <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg {...ICON_PROPS}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// PR 8: Roster-row response signal chips
// ---------------------------------------------------------------------------

/** Compact chip strip showing per-player "How'd it land?" reaction
 * counts on the Roster row (PR 8). Mirrors the mockup's Phase 6
 * coach roster (`⚡ 2 ✓ 1 · ? 1 needs help · 1 no response`).
 *
 * When the player has at least one `need_help` reaction, the row
 * surfaces a small orange "Follow up" CTA that opens the Development
 * modal so the coach can see the actual reflection bodies.
 *
 * Hides entirely (renders an em-dash placeholder) when no linked
 * account has responded — avoids a noisy "0 0 0" tile on rosters
 * with mostly self-account players. */
function RosterResponseChips({
  summary,
  onOpenDevelopment,
}: {
  summary?: { got_it: number; need_help: number; tried_this: number };
  onOpenDevelopment: () => void;
}) {
  const got = summary?.got_it ?? 0;
  const need = summary?.need_help ?? 0;
  const tried = summary?.tried_this ?? 0;
  const total = got + need + tried;
  if (total === 0) {
    return (
      <span
        className="text-ink-muted text-xs"
        data-testid="roster-response-chips-empty"
      >
        —
      </span>
    );
  }
  return (
    <div
      className="flex flex-wrap items-center gap-1"
      data-testid="roster-response-chips"
    >
      {got > 0 ? (
        <span
          className="inline-flex items-center gap-0.5 rounded-full bg-good/15 text-good px-1.5 py-0.5 text-[0.65rem] font-mono"
          title="Got it"
          data-signal="got_it"
        >
          <span aria-hidden="true">✓</span>
          {got}
        </span>
      ) : null}
      {tried > 0 ? (
        <span
          className="inline-flex items-center gap-0.5 rounded-full bg-accent/15 text-accent-ink px-1.5 py-0.5 text-[0.65rem] font-mono"
          title="I tried this"
          data-signal="tried_this"
        >
          <span aria-hidden="true">⚡</span>
          {tried}
        </span>
      ) : null}
      {need > 0 ? (
        <button
          type="button"
          onClick={onOpenDevelopment}
          className="inline-flex items-center gap-0.5 rounded-full bg-flag text-navy px-2 py-0.5 text-[0.65rem] font-display uppercase tracking-wider hover:opacity-90 transition-opacity"
          title={`${need} needs follow-up — open the player's plan`}
          data-testid="roster-response-follow-up"
          data-signal="need_help"
        >
          <span aria-hidden="true">?</span>
          {need} follow up
        </button>
      ) : null}
    </div>
  );
}
